const DEVICE_CODE_STORAGE_KEY = 'chaotuishou_aigc_device_code'

const SYSTEM_HEADER_CONSTANTS = {
  appCode: 'chaotuishou_aigc',
  clientVersion: '1.0.0',
  clientDeviceType: 'web',
  source: '7',
  platformType: '3',
  channel: 'default',
} as const

/** 当前客户端版本号，新版本更新比对以此为准 */
export const CLIENT_VERSION = SYSTEM_HEADER_CONSTANTS.clientVersion

type NavigatorWithUAData = Navigator & {
  userAgentData?: {
    platform?: string
    brands?: Array<{ brand: string; version: string }>
    mobile?: boolean
  }
}

export interface SystemHeaderPayload {
  appCode: string
  systemName: string
  systemVersion: string
  deviceName: string
  deviceCode: string
  clientVersion: string
  clientDeviceType: string
  messagePushCode: string
  source: string
  platformType: string
  channel: string
}

let cachedHeader = ''

/**
 * 生成并持久化终端设备号。用户退出登录不会清除，浏览器不清缓存则一直存在。
 */
export function getDeviceCode(): string {
  if (typeof window === 'undefined') {
    return ''
  }

  const existing = localStorage.getItem(DEVICE_CODE_STORAGE_KEY)?.trim()
  if (existing) {
    return existing
  }

  const deviceCode = generateDeviceCode()
  localStorage.setItem(DEVICE_CODE_STORAGE_KEY, deviceCode)
  return deviceCode
}

/** 构造每个请求必带的 `system` 请求头（JSON 字符串） */
export function getSystemHeader(): string {
  if (cachedHeader) {
    return cachedHeader
  }

  const deviceCode = getDeviceCode()
  const payload: SystemHeaderPayload = {
    appCode: SYSTEM_HEADER_CONSTANTS.appCode,
    systemName: getSystemName(),
    systemVersion: getSystemVersion(),
    deviceName: getDeviceName(),
    deviceCode,
    clientVersion: SYSTEM_HEADER_CONSTANTS.clientVersion,
    clientDeviceType: SYSTEM_HEADER_CONSTANTS.clientDeviceType,
    messagePushCode: deviceCode,
    source: SYSTEM_HEADER_CONSTANTS.source,
    platformType: SYSTEM_HEADER_CONSTANTS.platformType,
    channel: SYSTEM_HEADER_CONSTANTS.channel,
  }

  cachedHeader = JSON.stringify(payload)
  return cachedHeader
}

function generateDeviceCode(): string {
  const timestamp = Date.now().toString()
  let random = ''

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const buf = new Uint32Array(1)
    crypto.getRandomValues(buf)
    random = (buf[0] % 10_000_000).toString().padStart(7, '0')
  } else {
    random = Math.floor(Math.random() * 10_000_000)
      .toString()
      .padStart(7, '0')
  }

  return `${timestamp}${random}`
}

function getNavigator(): NavigatorWithUAData | null {
  if (typeof navigator === 'undefined') {
    return null
  }
  return navigator as NavigatorWithUAData
}

function getSystemName(): string {
  const nav = getNavigator()
  if (!nav) {
    return 'Unknown'
  }

  const hintPlatform = nav.userAgentData?.platform?.trim()
  if (hintPlatform) {
    return hintPlatform
  }

  const ua = nav.userAgent || ''
  if (/Windows/i.test(ua)) return 'Windows'
  if (/Mac OS X|Macintosh/i.test(ua)) return 'macOS'
  if (/Android/i.test(ua)) return 'Android'
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS'
  if (/Linux/i.test(ua)) return 'Linux'
  return nav.platform || 'Unknown'
}

function getSystemVersion(): string {
  const nav = getNavigator()
  if (!nav) {
    return ''
  }

  const ua = nav.userAgent || ''

  const windows = ua.match(/Windows NT ([\d.]+)/i)
  if (windows?.[1]) {
    return windows[1]
  }

  const mac = ua.match(/Mac OS X (\d+[._]\d+(?:[._]\d+)?)/i)
  if (mac?.[1]) {
    return mac[1].replace(/_/g, '.')
  }

  const android = ua.match(/Android ([\d.]+)/i)
  if (android?.[1]) {
    return android[1]
  }

  const ios = ua.match(/OS (\d+[._]\d+(?:[._]\d+)?) like Mac OS X/i)
  if (ios?.[1]) {
    return ios[1].replace(/_/g, '.')
  }

  return ''
}

function getDeviceName(): string {
  const nav = getNavigator()
  if (!nav) {
    return 'Web'
  }

  const brand = nav.userAgentData?.brands?.find(
    (item) => item.brand && !/not.?a.?brand/i.test(item.brand)
  )
  if (brand?.brand) {
    return brand.brand
  }

  const ua = nav.userAgent || ''
  if (/Edg\//i.test(ua)) return 'Edge'
  if (/Chrome\//i.test(ua)) return 'Chrome'
  if (/Firefox\//i.test(ua)) return 'Firefox'
  if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) return 'Safari'
  return 'Web'
}
